import { useTranslation } from "react-i18next";
import { LoadingState, ErrorState } from "@/components/common";
import { EmptyState } from "@/components/ui";
import {
  HandshakeAdminSessionPanel,
  HandshakeSellerChat,
} from "@/components/handshake";
import { useAdminHandshakeSessionPanel } from "@/hooks/useAdminHandshakeSessionPanel";
import { useHandshakeSessionActions } from "@/hooks/useHandshakeSessionActions";
import { useSellerHandshakeSessionDetail } from "@/hooks/useHandshakeSessionDetail";

interface BookingHandshakePanelProps {
  sessionId: string;
  adminMode: boolean;
  canInteract: boolean;
  canSendMessage: boolean;
  /** When true, render without an inner card shell (for nesting in another card). */
  flush?: boolean;
}

export function BookingHandshakePanel({
  sessionId,
  adminMode,
  canInteract,
  canSendMessage,
  flush = false,
}: BookingHandshakePanelProps) {
  if (adminMode) {
    return (
      <AdminBookingHandshakePanel
        sessionId={sessionId}
        canInteract={canInteract}
        canSendMessage={canSendMessage}
        flush={flush}
      />
    );
  }

  return (
    <SellerBookingHandshakePanel
      sessionId={sessionId}
      canInteract={canInteract}
    />
  );
}

function AdminBookingHandshakePanel({
  sessionId,
  canInteract,
  canSendMessage,
  flush,
}: {
  sessionId: string;
  canInteract: boolean;
  canSendMessage: boolean;
  flush: boolean;
}) {
  const {
    data: session,
    isLoading,
    isError,
    refetch,
    sendMessage,
    isSending,
    cancelSession,
    isCancelling,
    messagesEndRef,
    showCancelConfirm,
    setShowCancelConfirm,
  } = useAdminHandshakeSessionPanel(sessionId);

  if (isLoading) {
    return <HandshakePanelLoading bare />;
  }
  if (isError) {
    return <HandshakePanelError onRetry={() => void refetch()} bare />;
  }
  if (!session) {
    return <HandshakePanelNotFound bare />;
  }

  return (
    <HandshakeAdminSessionPanel
      session={session}
      variant={flush ? "flush" : "page"}
      canCancel={canInteract}
      canSendMessage={canSendMessage}
      isSending={isSending}
      isCancelling={isCancelling}
      onSend={sendMessage}
      onCancelRequest={() => setShowCancelConfirm(true)}
      messagesEndRef={messagesEndRef}
      showCancelConfirm={showCancelConfirm}
      onCloseCancelConfirm={() => setShowCancelConfirm(false)}
      cancelSession={cancelSession}
    />
  );
}

function SellerBookingHandshakePanel({
  sessionId,
  canInteract,
}: {
  sessionId: string;
  canInteract: boolean;
}) {
  const { data: session, isLoading, isError, isFetching, refetch } =
    useSellerHandshakeSessionDetail(sessionId);
  const { sendMessage, isSending } = useHandshakeSessionActions(sessionId, {
    admin: false,
  });

  if (isLoading) {
    return <HandshakePanelLoading />;
  }
  if (isError) {
    return <HandshakePanelError onRetry={() => void refetch()} />;
  }
  if (!session) {
    return <HandshakePanelNotFound />;
  }

  return (
    <HandshakeSellerChat
      session={session}
      canInteract={canInteract}
      isSending={isSending}
      onSend={sendMessage}
      onRefresh={refetch}
      isRefreshing={isFetching && !isLoading}
    />
  );
}

function HandshakePanelLoading({ bare = false }: { bare?: boolean }) {
  const { t } = useTranslation("bookings");
  return (
    <div
      className={
        bare ? "py-10" : "border-t border-surface-100 px-4 py-6"
      }
    >
      <LoadingState message={t("handshake.loading")} />
    </div>
  );
}

function HandshakePanelError({
  onRetry,
  bare = false,
}: {
  onRetry: () => void;
  bare?: boolean;
}) {
  const { t } = useTranslation("bookings");
  return (
    <div
      className={
        bare ? "py-6" : "border-t border-surface-100 px-4 py-4"
      }
    >
      <ErrorState message={t("handshake.loadError")} onRetry={onRetry} />
    </div>
  );
}

function HandshakePanelNotFound({ bare = false }: { bare?: boolean }) {
  const { t } = useTranslation("bookings");
  return (
    <EmptyState
      size="sm"
      showIcon={false}
      title={t("handshake.notFound")}
      className={bare ? "py-6" : "border-t border-surface-100 px-4 py-4"}
    />
  );
}
